import { useState, useEffect, useCallback } from "react";
import { supabase } from "@/lib/supabase";

export interface RankUser {
  id: string;
  nickname: string;
  points: number;
  rank: number;
}

interface UsePointRankingReturn {
  rankings: RankUser[];
  topThree: RankUser[];
  loading: boolean;
  error: string | null;
  refreshRanking: () => Promise<void>;
}

export function usePointRanking(limit: number = 10): UsePointRankingReturn {
  const [rankings, setRankings] = useState<RankUser[]>([]);
  const [topThree, setTopThree] = useState<RankUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchRanking = useCallback(async () => {
    try {
      setError(null);

      const { data, error: fetchError } = await supabase
        .from("profiles")
        .select("id, nickname, points")
        .order("points", { ascending: false })
        .limit(limit);

      if (fetchError) throw fetchError;

      const ranked: RankUser[] = (data || []).map((user, idx) => ({
        id: user.id,
        nickname: user.nickname || "익명",
        points: user.points ?? 0,
        rank: idx + 1,
      }));

      setRankings(ranked);
      setTopThree(ranked.slice(0, 3));
    } catch {
      setError("랭킹을 불러오지 못했습니다");
    } finally {
      setLoading(false);
    }
  }, [limit]);

  useEffect(() => {
    fetchRanking();
  }, [fetchRanking]);

  const refreshRanking = useCallback(async () => {
    setLoading(true);
    await fetchRanking();
  }, [fetchRanking]);

  return {
    rankings,
    topThree,
    loading,
    error,
    refreshRanking,
  };
}